import { AxiosErrorResponse } from "@/@types/Errors";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { ForgotPasswordService } from "@/services/forgot-password.service";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { IForgotPassword } from "@/@types/ForgotPassword";
import { useAuth } from "@/auth/Auth";
import { useNavigate } from "react-router-dom";

export const useForgotPassword = () => {
  const { t } = useTranslation(["common", "validation"]);

  const { saveEmail } = useAuth();

  const RecoverPasswordSchema = z.object({
    email: z
      .string()
      .min(1, { message: t("required_field") })
      .email({ message: t("validation:invalid_email") }),
  });

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<IForgotPassword>({
    resolver: zodResolver(RecoverPasswordSchema),
  });

  const { toast } = useToast();
  const { forgotPassword } = ForgotPasswordService;
  const navigate = useNavigate();

  const { mutate, isPending, isError, isSuccess } = useMutation({
    mutationFn: async (data: IForgotPassword): Promise<void> => {
      await forgotPassword(data);
      saveEmail(data.email);
    },
    onSuccess: () => {
      toast({
        variant: "success",
        description: t("email_sent"),
      });
      navigate("/verify-password");
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      if (error.response?.status === 422) {
        Object.keys(error.response.data.errors).forEach((key: any) => {
          toast({
            variant: "error",
            description: error.response.data.errors[key][0],
          });
        });
      } else {
        toast({
          variant: "error",
          description: error?.message,
        });
      }
    },
  });

  const onSubmit = (data: IForgotPassword) => {
    mutate(data);
  };

  const defaultValues = watch();

  useEffect(() => {
    if (defaultValues?.email) {
      clearErrors("email");
    }
  }, [defaultValues?.email]);

  return {
    onSubmit,
    handleSubmit,
    setValue,
    register,
    errors,
    isPending,
    isError,
    isSuccess,
  };
};
